import * as yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { emailRegex, getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import UI from '../../../../common/UI';
import WebsiteInput from '../../../Common/WebsiteInput';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

const editEventInfoSchema = yup.object().shape({
  title: yup.string().required(),
  ...dateRangeObject(true, false),
  location: yup.object().required().shape({
    id: yup.string().required(),
  }),
  email: yup.string().nullable().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
});

export interface EditEventInfoFormProps {
  event: Event;
}

const EditEventInfoForm = ({ event }: EditEventInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (event: Event) => ({
    title: event.title,
    title_translations: translationsForAttribute(event.translations, 'title'),
    description: event.description,
    description_translations: translationsForAttribute(event.translations, 'description'),
    email: event.email,
    website_url: event.website_url,
    location: {
      id: event.location.id,
      title: event.location.title,
      description: event.location.description,
      address: event.location.address,
      country: event.location.country,
      coordinates: event.location.coordinates,
    },
    start_date: event.start_date,
    end_date: event.start_date === event.end_date ? null : event.end_date,
    private: event.private,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      info: {
        ...form.values,
        end_date: form.values.end_date || form.values.start_date,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const locationBlurTimerRef = useRef<number>();

  // Clear timer on unmount.
  useEffect(() => () => window.clearTimeout(locationBlurTimerRef.current), []);

  const handleLocationBlur = () => {
    window.clearTimeout(locationBlurTimerRef.current);

    // Save the touched event after a small delay, to prevent flashing of validation errors.
    locationBlurTimerRef.current = window.setTimeout(() => {
      form.setTouched('location', true);
    }, 300);
  };

  const { valid, errors } = useValidation({
    schema: editEventInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'info'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('event')}
          </UI.Legend>
          <UI.InputDescription>
            {t('edit_event_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
            <UI.InputLabel htmlFor="event_title">
              {t('event_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TranslatableInput
              form={form}
              errors={errors}
              name="title"
              id="event_title"
              sc={{ size: 'lg' }}
              defaultLocale={event.locale}
              placeholder={t('event_title_placeholder')}
            />
            <UI.ErrorMessages attribute={t('event_title')} errors={errors.title} />
          </UI.InputGroup>

          <DateRangeForm form={form} errors={errors} requireStart allowFullDay />

          <UI.InputGroup sc={{ valid: !errors['location.id'], touched: form.touched.location }}>
            <UI.InputLabel htmlFor="location">
              {t('location')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.GoogleLocationInput
              id="location"
              value={form.values.location}
              onChange={(location) => form.set('location', location)}
              onBlur={() => handleLocationBlur()}
              placeholder={t('location_placeholder')}
            />
            <UI.ErrorMessages attribute={t('location')} errors={errors['location.id']} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
            <UI.InputLabel id="EventDescriptionLabel">
              {t('description')}
            </UI.InputLabel>
            <TranslatableEditor
              form={form}
              errors={errors}
              name="description"
              defaultLocale={event.locale}
              title={t('description')}
              attributes={{
                'aria-labelledby': 'EventDescriptionLabel',
              }}
            />
            <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
            <UI.InputLabel htmlFor="email_address">
              {t('email_address')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('event_email_description')}
            </UI.InputDescription>
            <UI.DebouncedInput
              {...form.text('email')}
              placeholder={project.organisation_email}
              id="email_address"
            />
            <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.website_url, touched: form.touched.website_url }}>
            <UI.InputLabel htmlFor="website">
              {t('website')}
            </UI.InputLabel>
            <WebsiteInput form={form} formKey="website_url" id="website" />
            <UI.ErrorMessages attribute={t('website')} errors={errors.website_url} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.private, touched: form.touched.private }}>
            <UI.InputLabel htmlFor="private">
              {t('event_visibility')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('private_event_description')}
            </UI.InputDescription>
            <UI.Checkbox {...form.checkbox('private')} id="private">
              {t('private_event')}
            </UI.Checkbox>
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventInfoForm;
